/**
 * Social media post option sets + colors.
 *
 * Status is driven by the marketing publish pipeline lifecycle
 * (draft → scheduled → publishing → posted / partially_failed / failed /
 * canceled), not editable freely inline.
 *
 * Brand and platform options are DERIVED from the canonical sources so this
 * config can never drift from the pipeline / DB enum:
 *   - brands:    injected at render time by useSocialPostsConfigEnhancer, because
 *                brands are database rows (see lib/marketing/brand-registry.ts)
 *   - platforms: SOCIAL_PLATFORMS (@/lib/integrations/social/types — the exact
 *                keys persisted in social_media_posts.platforms)
 */

import { SOCIAL_PLATFORMS } from "@/lib/integrations/social/types";
import {
  SOCIAL_POST_STATUS_VALUES,
  type SocialPostStatus,
} from "@/lib/constants/social-post-statuses";

const SOCIAL_POST_STATUS_META: Record<
  SocialPostStatus,
  { label: string; color: string }
> = {
  draft: { label: "Draft", color: "gray" },
  scheduled: { label: "Scheduled", color: "blue" },
  publishing: { label: "Publishing", color: "indigo" },
  posted: { label: "Posted", color: "green" },
  partially_failed: { label: "Partially Failed", color: "amber" },
  failed: { label: "Failed", color: "red" },
  canceled: { label: "Canceled", color: "orange" },
};

/** UI status options — values from `SOCIAL_POST_STATUS_VALUES`. */
export const SOCIAL_POST_STATUS_OPTIONS = SOCIAL_POST_STATUS_VALUES.map(
  (value) => ({
    label: SOCIAL_POST_STATUS_META[value].label,
    value,
    color: SOCIAL_POST_STATUS_META[value].color,
  })
);

/**
 * Brand options are EMPTY here on purpose.
 *
 * Brands are rows in `marketing_brands` now, so a module-level constant would
 * freeze the list at build time and a brand onboarded today would be missing
 * from the filter, the badge and the inline editor. `useSocialPostsConfigEnhancer`
 * injects the live list into this field at render time — the same mechanism the
 * contacts/tickets tag filters already use.
 */
export const SOCIAL_POST_BRAND_OPTIONS: { label: string; value: string }[] = [];

export const SOCIAL_POST_MEDIA_KIND_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Image", value: "image" },
  { label: "Video", value: "video" },
] as const;

/** Human-readable labels for the canonical social platform keys. */
const PLATFORM_LABELS: Record<string, string> = {
  facebook: "Facebook",
  twitter: "X (Twitter)",
  google_business: "Google Business",
  instagram: "Instagram",
  bluesky: "Bluesky",
  youtube: "YouTube",
  linkedin: "LinkedIn",
};

/**
 * Platform options derived from SOCIAL_PLATFORMS (the exact `platforms` text[]
 * keys). `platforms` is a text[] column, so this powers a multi-value filter.
 */
export const SOCIAL_POST_PLATFORM_OPTIONS: { label: string; value: string }[] =
  SOCIAL_PLATFORMS.map((platform) => ({
    label: PLATFORM_LABELS[platform] ?? platform,
    value: platform,
  }));

/** Statuses that mean the post has not yet been published (NOT terminal). */
export const PENDING_PUBLISH_STATUSES = ["draft", "scheduled", "publishing"] as const;
